/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { computed, observable, action } from 'mobx'
import { injectable } from 'inversify'
import { injectProperty } from '../../arc/configuration/ServiceLocator'
import { IRequestHistoryStateManager, INpi, IPayer, ICoverageDiscoveryRequestHistoryGridCriteria, ICoverageDiscoveryRequestHistoryGridResults, IRunDateDropdown, ICoverageDiscoveryViewReportGridResults, IReviewedData, ITransactionStatus, IEligibilityStatus } from './contracts'
import { ValidationResult } from '../../arc/utils/ValidationResult/ValidationResult'
import { IRequestHistoryApiClient } from '../RequestHistoryApiClient/contracts'
import { ITransactionStatusApiClient } from '../TransactionStatusApiClient/contracts'
import { IEligibilityStatusApiClient } from '../EligibilityStatusApiClient/contracts'
import { IAccountFacilityApiClient } from '../AccountFacilityApiClient/contracts'
import { IPayersApiClient } from '../PayersApiClient/contracts'
import { IExportRequestApiClient, IExportRequest } from '../ExportRequestApiClient/contracts'
import { IViewReportApiClient } from '../ViewReportApiClient/contracts'

@injectable()
export class RequestHistoryStateManager implements IRequestHistoryStateManager {
	@observable
	isDataLoading: boolean

	@observable
	transactionExportRequestForJob: IExportRequest

	@observable
	runDateDropdownList: IRunDateDropdown[]

	@observable
	availableNpis: INpi[]

	@observable
	availablePayers: IPayer[]

	@observable
	availableTransactionStatuses: ITransactionStatus[]

	@observable
	availableEligibilityStatuses: IEligibilityStatus[]

	@observable
	checkExportRequestSubmission: boolean

	@observable
	isReportAvailableToBeViewed: boolean

	@observable
	selectedTransactionIds: Set<number>

	@observable
	selectedPayerIds: Set<number>

	@observable
	selectedNpiIds: Set<number>

	@observable
	selectedStatusIds: Set<number>

	@observable
	reviewedData: IReviewedData

	@observable
	coverageDiscoveryRequestHistoryGridCriteria: ICoverageDiscoveryRequestHistoryGridCriteria

	@observable
	coverageDiscoveryRequestHistoryGridResults: ICoverageDiscoveryRequestHistoryGridResults

	@observable
	coverageDiscoveryViewReportGridResults: ICoverageDiscoveryViewReportGridResults

	@injectProperty('IRequestHistoryApiClient')
	public RequestHistoryApiClient!: IRequestHistoryApiClient

	@injectProperty('ITransactionStatusApiClient')
	public TransactionStatusApiClient!: ITransactionStatusApiClient

	@injectProperty('IEligibilityStatusApiClient')
	public EligibilityStatusApiClient!: IEligibilityStatusApiClient

	@injectProperty('IAccountFacilityApiClient')
	public NpisApiClient!: IAccountFacilityApiClient

	@injectProperty('IPayersApiClient')
	public PayersApiClient!: IPayersApiClient

	@injectProperty('IExportRequestApiClient')
	public ExportRequestApiClient!: IExportRequestApiClient

	@injectProperty('IViewReportApiClient')
	public ViewReportApiClient!: IViewReportApiClient

	constructor() {
		this.isDataLoading = false
		this.runDateDropdownList = []
		this.availableNpis = []
		this.availablePayers = []
		this.availableTransactionStatuses = []
		this.availableEligibilityStatuses = []
		this.reviewedData = {
			reviewedSelected: false,
			rowIdForReviewed: 0,
		}
		this.coverageDiscoveryRequestHistoryGridCriteria = {
			searchTerm: {
				firstName: '',
				lastName: '',
				ssn: '',
				npis: [],
				payers: [],
				transactionStatus: [],
				eligibilityStatus: [],
			},
			page: 1,
			sizePerPage: 10,
			sortField: 'id',
			sortOrder: 'desc',
			dateRange: null,
		}
		this.coverageDiscoveryRequestHistoryGridResults = {
			items: [],
			total: 0,
		}
		this.coverageDiscoveryViewReportGridResults = {
			items: [],
			total: 0,
		}
		this.selectedTransactionIds = new Set()
		this.selectedPayerIds = new Set()
		this.selectedNpiIds = new Set()
		this.selectedStatusIds = new Set()
		this.checkExportRequestSubmission = false
		this.isReportAvailableToBeViewed = true
		this.transactionExportRequestForJob = {
			transactionIds: [],
			batchRunId: 0,
		}
	}

	@computed get validateSelectedRequestDetail(): ValidationResult {
		const result = new ValidationResult()

		return result
	}

	private setSelectedTransactionsOnResponse(response: ICoverageDiscoveryRequestHistoryGridResults): ICoverageDiscoveryRequestHistoryGridResults {
		response.items = response.items.map(item => {
			item.isSelected = this.selectedTransactionIds.has(item.id)
			return item
		})
		return response
	}

	@action loadViewReportGridresults(): Promise<void> {
		this.isDataLoading = true
		this.coverageDiscoveryViewReportGridResults.items = []
		return this.ViewReportApiClient.getReportDetailsOnCompletion()
			.then(response => {
				if (response.total > 0) {
					this.setViewReportGridResults(response)
					this.isReportAvailableToBeViewed = false
				} else {
					this.coverageDiscoveryViewReportGridResults.items = []
					this.isReportAvailableToBeViewed = true
				}
				this.isDataLoading = false
			})
			.catch(error => {
				this.isDataLoading = false
				console.log(error)
				throw new Error('An error occurred when listing the reports')
			})
	}
	@action deleteSelectedReport(id: number): Promise<void> {
		return this.ViewReportApiClient.deleteSelectedReport(id).catch(error => {
			console.log(error)
			throw new Error('An error occurred when deleting the report')
		})
	}
	@action downloadSelectedReport(id: number): Promise<any> {
		return this.ExportRequestApiClient.getGeneratedExportReport(id)
	}

	@action setViewReportGridResults(results: ICoverageDiscoveryViewReportGridResults): void {
		this.coverageDiscoveryViewReportGridResults = results
	}

	@action loadCoverageDiscoveryRequestHistoryGridResults(batchRunId: number): Promise<void> {
		this.isDataLoading = true
		return this.RequestHistoryApiClient.list(batchRunId, this.coverageDiscoveryRequestHistoryGridCriteria)
			.then(response => {
				response = this.setSelectedTransactionsOnResponse(response)
				this.setItemsGridResults(response)
				this.isDataLoading = false
			})
			.catch(error => {
				this.isDataLoading = false
				console.log(error)
				throw new Error('An error occurred when listing the history')
			})
	}

	@action updateReviewed(id: number, reviewed: boolean): Promise<any> {
		this.reviewedData.reviewedSelected = reviewed
		this.reviewedData.rowIdForReviewed = id
		return this.RequestHistoryApiClient.updateReviewed(this.reviewedData).catch(error => {
			console.log(error)
			throw new Error('An unexpected error occured while changing the reviewed.')
		})
	}

	@action setTransactionIdSelected(id: number, selected: boolean): void {
		if (selected) {
			this.selectedTransactionIds.add(id)
		} else {
			this.selectedTransactionIds.delete(id)
		}
	}

	@action sendSelectedTransactionIdsForLessThanLimit(item: number[]): Promise<any> {
		const request = { transactionIds: item } as IExportRequest
		return this.ExportRequestApiClient.getTransactionReport(request)
	}

	@action sendSelectedTransactionIdsForMoreThanLimit(fileName: string, batchRunId: number, item: number[]): Promise<void> {
		this.transactionExportRequestForJob.fileName = fileName
		this.transactionExportRequestForJob.batchRunId = batchRunId
		this.transactionExportRequestForJob.transactionIds = item

		return this.ExportRequestApiClient.sendSelectedTransactionIdsForExportJob(this.transactionExportRequestForJob)
			.catch(error => {
				console.log(error)
				this.checkExportRequestSubmission = false
				throw new Error('An error occurred when saving the requests')
			})
			.then(() => {
				this.checkExportRequestSubmission = true
			})
	}

	@action setRequestHistoryGridCriteriaAndLoad(batchRunId: number, criteria: ICoverageDiscoveryRequestHistoryGridCriteria): Promise<void> {
		this.coverageDiscoveryRequestHistoryGridCriteria = criteria
		return this.loadCoverageDiscoveryRequestHistoryGridResults(batchRunId)
	}

	@action setRequestHistoryGridCriteria(criteria: ICoverageDiscoveryRequestHistoryGridCriteria): Promise<void> {
		this.coverageDiscoveryRequestHistoryGridCriteria = criteria
		return new Promise<void>(resolve => resolve())
	}

	@action setItemsGridResults(results: ICoverageDiscoveryRequestHistoryGridResults): void {
		this.coverageDiscoveryRequestHistoryGridResults = results
	}

	@action loadAvailableNpis(): Promise<void> {
		return this.NpisApiClient.getAllNpis()
			.then(response => {
				this.setAvailbleNpis(response)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when listing the npis')
			})
	}

	@action setAvailbleNpis(availableNpis: INpi[]): void {
		this.availableNpis = []
		this.availableNpis = availableNpis
	}

	@action loadAvailablePayers(): Promise<void> {
		return this.PayersApiClient.getAllPayers()
			.then(response => {
				this.setAvailblePayers(response)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when listing the payers')
			})
	}

	@action setAvailblePayers(availablePayers: IPayer[]): void {
		this.availablePayers = []
		this.availablePayers = availablePayers
	}

	@action loadAvailableTransactionStatuses(): Promise<void> {
		return this.TransactionStatusApiClient.getAllTransactionStatuses()
			.then(response => {
				this.setTransactionStatuses(response)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when listing the statuses')
			})
	}

	@action setTransactionStatuses(availableStatuses: ITransactionStatus[]): void {
		this.availableTransactionStatuses = []
		this.availableTransactionStatuses = availableStatuses
	}

	@action loadAvailableEligibilityStatuses(): Promise<void> {
		return this.EligibilityStatusApiClient.getAllEligibilityStatuses()
			.then(response => {
				this.setEligibilityStatuses(response)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when listing the statuses')
			})
	}

	@action setEligibilityStatuses(availableStatuses: IEligibilityStatus[]): void {
		this.availableEligibilityStatuses = []
		this.availableEligibilityStatuses = availableStatuses.map(item => ({ ...item, isSelected: false }))
		this.availableEligibilityStatuses = this.availableEligibilityStatuses.map(x => (x.name === 'Not Found' ? { ...x, name: 'Not Found by a Payer' } : x))
	}

	@action setNpiValue(id: number, selected: boolean): void {
		if (selected) {
			this.selectedNpiIds.add(id)
		} else {
			this.selectedNpiIds.delete(id)
		}
	}

	@action setPayerValue(id: number, selected: boolean): void {
		if (selected) {
			this.selectedPayerIds.add(id)
		} else {
			this.selectedPayerIds.delete(id)
		}
	}

	@action setTransactionStatus(id: number, selected: boolean): void {
		if (selected) {
			this.selectedStatusIds.add(id)
		} else {
			this.selectedStatusIds.delete(id)
		}
	}

	@action setEligibilityStatus(id: number, selected: boolean): void {
		if (selected) {
			this.selectedStatusIds.add(id)
		} else {
			this.selectedStatusIds.delete(id)
		}
	}

	@action loadRunDatesRange(): void {
		this.runDateDropdownList = []
		this.runDateDropdownList.push(
			{
				id: 0,
				selectionText: '--select/ within--',
			},
			{
				id: 1,
				selectionText: '1 day',
			},
			{
				id: 2,
				selectionText: '3 days',
			},
			{
				id: 3,
				selectionText: '1 week',
			},
			{
				id: 4,
				selectionText: '2 weeks',
			},
			{
				id: 5,
				selectionText: '1 month',
			},
			{
				id: 6,
				selectionText: '6 months',
			},
			{
				id: 7,
				selectionText: '1 year',
			},
		)
	}
}
